<template>
  <div class="check-in-page">
    <!-- Camera Section -->
    <div class="camera-container">
      <!-- QR Code Scanner -->
      <QrcodeStream
        v-if="!processing"
        :constraints="{ facingMode: 'user' }"
        class="qr-scanner"
        @detect="onDetect"
        @camera-on="onCameraReady"
        @error="onCameraError"
      >
        <!-- Scanning Overlay -->
        <div class="scanning-overlay">
          <div class="scan-frame">
            <div class="corner corner-tl" />
            <div class="corner corner-tr" />
            <div class="corner corner-bl" />
            <div class="corner corner-br" />

            <div class="scan-line" />
          </div>
        </div>

        <div
          class="mx-4 my-4 px-4 py-4 rounded d-flex gap-4"
          style="background: rgba(0, 0, 0, 60%);"
        >
          <VChip
            color="primary"
            prepend-icon="tabler-arrow-left"
            size="large"
            @click="onClickBack"
          >
            Back
          </VChip>
          <VChip
            color="primary"
            prepend-icon="tabler-door-enter"
            size="large"
          >
            Visitor Check-In
          </VChip>

          <VSpacer />

          <VChip
            color="success"
            prepend-icon="tabler-scan"
            size="large"
            class="mr-2"
          >
            Scanning Active
          </VChip>
          <VChip
            :color="isDeviceOnline ? 'success' : 'error'"
            :prepend-icon="isDeviceOnline ? 'tabler-wifi' : 'tabler-exclamation-circle'"
            size="large"
          >
            Device {{ isDeviceOnline ? 'Online' : 'Offline' }}
          </VChip>
        </div>

        <!-- Left Side Badges -->
        <!--
          <div class="badges-left">
          <VChip
          color="primary"
          prepend-icon="tabler-arrow-left"
          size="large"
          >
          Back
          </VChip>
          <VChip
          color="primary"
          prepend-icon="tabler-login"
          size="large"
          >
          Visitor Check-In
          </VChip>
          </div> 
        -->

        <!-- Right Side Badges -->
        <!--
          <div class="badges-right">
          <VChip
          color="success"
          prepend-icon="tabler-scan"
          size="large"
          class="mr-2"
          >
          Scanning Active
          </VChip>
          <VChip
          color="success"
          prepend-icon="tabler-wifi"
          size="large"
          >
          Device Online
          </VChip>
          </div> 
        -->
      </QrcodeStream>

      <!-- Processing State -->
      <div
        v-else
        class="camera-placeholder"
      >
        <VProgressCircular
          indeterminate
          size="64"
          color="primary"
          class="mb-4"
        />
        <h3 class="text-h6 mb-2">
          Processing Check-In...
        </h3>
        <p class="text-body-2 text-medium-emphasis">
          Please wait while we verify your QR code
        </p>
      </div>
    </div>

    <!-- Processing Dialog -->
    <VDialog
      v-model="processing"
      width="400"
      persistent
    >
      <VCard>
        <VCardText class="text-center pa-8">
          <VProgressCircular
            indeterminate
            size="64"
            color="primary"
            class="mb-4"
          />
          <h3 class="text-h6 mb-2">
            Processing Check-In...
          </h3>
          <p class="text-body-2 text-medium-emphasis mb-0">
            Please wait while we verify your QR code
          </p>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Success Dialog -->
    <VDialog
      v-model="showSuccessDialog"
      width="500"
      persistent
    >
      <VCard>
        <VCardText class="text-center">
          <VIcon
            icon="tabler-circle-check"
            size="60"
            color="success"
            class="mb-2"
          />
          <h2 class="text-h4 font-weight-bold text-success">
            Check-In Successful!
          </h2>
        </VCardText>

        <VDivider />

        <VCardText>
          <div class="d-flex flex-column gap-2">
            <div class="text-body-1">
              <strong>Visitor:</strong> {{ successData?.visitor?.name || 'N/A' }}
            </div>
            <div class="text-body-1">
              <strong>Check-In Time:</strong> {{ successData?.session?.checked_in_at ? moment(successData.session.checked_in_at).format('h:mm A') : 'N/A' }}
            </div>
            <div class="text-body-1">
              <strong>Expected Check-Out Time:</strong> {{ successData?.expected_checkout ? moment(successData.expected_checkout).format('h:mm A') : 'N/A' }}
            </div>
            <div class="text-body-1">
              <strong>Badge Number:</strong> {{ successData?.badge_number || 'N/A' }}
            </div>
          </div>
        </VCardText>

        <VDivider />

        <VCardText class="text-center">
          <div class="text-body-2 text-medium-emphasis">
            This dialog will close automatically in {{ successCountdown }} seconds
          </div>
        </VCardText>

        <!--
          <VCardText class="text-center">
          <VIcon
          icon="tabler-circle-check"
          size="80"
          color="success"
          class="mb-4"
          />
          <h2 class="text-h4 font-weight-bold mb-2 text-success">
          Check-In Successful!
          </h2>
          <VDivider class="my-4" />
          <div class="text-start">
          <p class="text-body-1 mb-2">
          <strong>Visitor:</strong> {{ successData?.visitor?.name || 'N/A' }}
          </p>
          <p class="text-body-1 mb-2">
          <strong>Check-In Time:</strong> {{ successData?.session?.checked_in_at ? moment(successData.session.checked_in_at).format('h:mm A') : 'N/A' }}
          </p>
          <p class="text-body-1 mb-2">
          <strong>Expected Check-Out Time:</strong> {{ successData?.expected_checkout ? moment(successData.expected_checkout).format('h:mm A') : 'N/A' }}
          </p>
          <p class="text-body-1 mb-2">
          <strong>Badge Number:</strong> {{ successData?.badge_number || 'N/A' }}
          </p>
          </div>
          <VDivider class="my-4" />
          <p class="text-body-2 text-medium-emphasis mt-4">
          This dialog will close automatically in {{ successCountdown }} seconds
          </p>
          </VCardText> 
        -->
      </VCard>
    </VDialog>

    <!-- Error Dialog -->
    <VDialog
      v-model="showErrorDialog"
      width="500"
      persistent
    >
      <VCard>
        <VCardTitle class="bg-error d-flex align-center">
          <VIcon
            icon="tabler-alert-circle"
            class="me-2"
          />
          Check-In Failed
        </VCardTitle>
        <VCardText class="pa-6">
          <VAlert
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ errorMessage }}
          </VAlert>
          <p class="text-body-2 text-medium-emphasis">
            Please try scanning again or contact the reception desk for assistance.
          </p>
        </VCardText>
        <VCardActions class="d-flex flex-column">
          <!-- <VSpacer /> -->
          <VBtn
            color="error"
            variant="flat"
            block
            @click="closeErrorDialog"
          >
            Try Again
          </VBtn>
          <div class="text-body-2 text-medium-emphasis mt-1">
            This dialog will close automatically in {{ errorCountdown }} seconds
          </div>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Sound effects (hidden) -->
    <audio
      ref="successSound"
      src="/sounds/kiosk/success.mp3"
      preload="auto"
    />
    <audio
      ref="errorSound"
      src="/sounds/kiosk/error.mp3"
      preload="auto"
    />
  </div>
</template>

<script setup>
import { kioskAuthState } from '@/@core/auth/kioskAuthState'
import { useToast } from '@/composables/useToast'
import moment from 'moment'
import { QrcodeStream } from 'vue-qrcode-reader'
import { useRouter } from 'vue-router'

// Composables
const router = useRouter()
const toast = useToast()

// Define page metadata
definePage({
  meta: {
    layout: 'blank',
    public: true,
    unauthenticatedOnly: true,
    title: 'Visitor Check-In',
  },
})

const auth = kioskAuthState()

// Page Accessability Checking
if (!auth.token) {
  router.push({
    name: 'care-visit-kiosk-registration',
  })
}

// Refs
const successSound = ref(null)
const errorSound = ref(null)

// State
const processing = ref(false)
const showSuccessDialog = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')
const errorCountdown = ref(3)
const successData = ref(null)
const successCountdown = ref(3)
const isDeviceOnline = ref(true)

// Cooldown management
let lastScannedCode = null
let lastScanTime = 0
let countdownInterval = null
const SCAN_COOLDOWN = 3000 // 3 seconds cooldown between scans

// Methods
const onCameraReady = () => {
  console.log('Camera is ready')

  // toast.success('Camera initialized successfully')
}

const onCameraError = error => {
  console.error('Camera error:', error)

  let errorMsg = 'Failed to access camera.'

  if (error.name === 'NotAllowedError') {
    errorMsg = 'Camera access denied. Please allow camera permissions.'
  } else if (error.name === 'NotFoundError') {
    errorMsg = 'No camera found on this device.'
  } else if (error.name === 'NotReadableError') {
    errorMsg = 'Camera is already in use by another application.'
  } else if (error.name === 'OverconstrainedError') {
    errorMsg = 'Camera constraints not supported.'
  }

  toast.error(errorMsg)
}

const onDetect = async detectedCodes => {
  console.log('🔍 Detect event fired!', detectedCodes)

  if (processing.value) {
    console.log('⏸️ Already processing, skipping...')
    
    return
  }

  if (!detectedCodes || detectedCodes.length === 0) {
    console.log('❌ No codes detected')
    
    return
  }

  // Get the first detected QR code
  const firstCode = detectedCodes[0]
  const qrData = firstCode.rawValue

  console.log('✅ QR Code detected:', qrData)

  // Check cooldown to prevent duplicate scans
  const now = Date.now()
  if (lastScannedCode === qrData && (now - lastScanTime) < SCAN_COOLDOWN) {
    console.log('⏰ Cooldown active, skipping duplicate scan')
    
    return
  }

  lastScannedCode = qrData
  lastScanTime = now

  console.log('🚀 Processing check-in...')
  processing.value = true

  // * Check In API Call
  await $api('/care-visit/kiosk/check-in/qr', {
    method: 'POST',
    body: JSON.stringify({
      qr_token: qrData,
    }),
  }).then(({ success, message, data }) => {
    console.log('✅ API Response:', success, message, data)

    // Simulated response (replace with actual API response)
    // const response = {
    //   data: {
    //     success: true,
    //     visitor_name: 'John Doe',
    //     check_in_time: new Date().toLocaleTimeString(),
    //     location: 'Main Entrance',
    //     visitor_id: '12345',
    //     qr_code: qrData,
    //   },
    // }

    processing.value = false

    if (success) {
      successData.value = data
      showSuccessDialog.value = true

      // Play success sound
      playSound(successSound.value)

      // Start countdown
      startSuccessCountdown()
    }
  }).catch(error => {
    processing.value = false
    console.error('❌ Check-in error:', error)

    // errorMessage.value = error.response?.data?.message || error.message || 'Failed to process check-in'
    errorMessage.value = 'Failed to process check-in'
    showErrorDialog.value = true

    // Play error sound
    playSound(errorSound.value)

    // Resume scanning by resetting processing
    setTimeout(() => {
      processing.value = false
    }, 3000)
  })
}

const startSuccessCountdown = () => {
  successCountdown.value = 3

  countdownInterval = setInterval(() => {
    successCountdown.value--

    if (successCountdown.value <= 0) {
      clearInterval(countdownInterval)
      closeSuccessDialog()
    }
  }, 1000)
}

const closeSuccessDialog = () => {
  showSuccessDialog.value = false
  successData.value = null

  if (countdownInterval) {
    clearInterval(countdownInterval)
  }

  // Resume scanning by setting processing to false
  setTimeout(() => {
    processing.value = false
  }, 500)
}

const closeErrorDialog = () => {
  showErrorDialog.value = false
  errorMessage.value = ''

  // Scanner will automatically resume when processing becomes false
  processing.value = false
}

const startErrorCountdown = () => {
  errorCountdown.value = 3

  countdownInterval = setInterval(() => {
    errorCountdown.value--

    if (errorCountdown.value <= 0) {
      clearInterval(countdownInterval)
      closeErrorDialog()
    }
  }, 1000)
}

watch(() => showErrorDialog.value, value => {
  if (value === true) {
    startErrorCountdown()
  }
})

const playSound = audioElement => {
  if (audioElement) {
    audioElement.currentTime = 0
    audioElement.play().catch(err => {
      console.log('Audio play failed:', err)
    })
  }
}

const onClickBack = () => {
  router.push({ name: 'care-visit-kiosk-panel' })
}

// * Call Heartbeat API In Every 5 Minutes
const heartbeat = () => {
  setInterval(async () => {
    await $api('/care-visit/kiosk/heartbeat', {
      method: 'POST',
    }).then(({}) => {
      isDeviceOnline.value = true
    }).catch(error => {
      isDeviceOnline.value = false
    })
  }, 300000)
}

// Lifecycle
onMounted(() => {
  // heartbeat()

  console.log('✅ Check-in page mounted')
  console.log('📦 QrcodeStream component imported:', QrcodeStream)
})

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
  console.log('👋 Check-in page unmounted')
})
</script>

<style scoped>
.check-in-page {
  display: flex;
  overflow: hidden;
  flex-direction: column;
  background: #000;
  block-size: 100vh;
}

.camera-container {
  position: relative;
  display: flex;
  overflow: hidden;
  flex: 1;
  align-items: center;
  justify-content: center;
  background: #000;
}

.camera-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  block-size: 100%;
  color: white;
  inline-size: 100%;
  text-align: center;
}

.qr-scanner {
  position: relative;
  block-size: 100%;
  inline-size: 100%;
}

.badges-left {
  position: absolute;
  z-index: 10;
  display: flex;
  gap: 0.5rem;
  inset-block-start: 1rem;
  inset-inline-start: 1rem;
}

.badges-right {
  position: absolute;
  z-index: 10;
  display: flex;
  gap: 0.5rem;
  inset-block-start: 1rem;
  inset-inline-end: 1rem;
}

.scanning-overlay {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  inset: 0;
  pointer-events: none;
}

.scan-frame {
  position: relative;
  border: 2px solid rgba(255, 255, 255, 50%);
  block-size: 300px;
  inline-size: 300px;
}

.corner {
  position: absolute;
  border: 3px solid #4caf50;
  block-size: 30px;
  inline-size: 30px;
}

.corner-tl {
  border-block-end: none;
  border-inline-end: none;
  inset-block-start: -2px;
  inset-inline-start: -2px;
}

.corner-tr {
  border-block-end: none;
  border-inline-start: none;
  inset-block-start: -2px;
  inset-inline-end: -2px;
}

.corner-bl {
  border-block-start: none;
  border-inline-end: none;
  inset-block-end: -2px;
  inset-inline-start: -2px;
}

.corner-br {
  border-block-start: none;
  border-inline-start: none;
  inset-block-end: -2px;
  inset-inline-end: -2px;
}

.scan-line {
  position: absolute;
  animation: scan 2s ease-in-out infinite;
  background: linear-gradient(90deg, transparent, #4caf50, transparent);
  block-size: 2px;
  inline-size: 100%;
}

@keyframes scan {
  0% {
    inset-block-start: 0;
  }

  50% {
    inset-block-start: 100%;
  }

  100% {
    inset-block-start: 0;
  }
}


.text-medium-emphasis {
  opacity: 0.68;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .scan-frame {
    block-size: 250px;
    inline-size: 250px;
  }

  .camera-container {
    min-block-size: 50vh;
  }

  .video-wrapper {
    block-size: 50vh;
  }
}
</style>
